import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { finalize, Subscription } from 'rxjs';
import { Product, ProductImage } from '../../core/models/product.models';
import { ProductsService, ReviewQueueStatus } from '../../core/services/products.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { CurrentUserService } from '../../core/auth/current-user.service';

@Component({
  selector: 'app-listing-approvals',
  imports: [FormsModule, RouterLink],
  templateUrl: './listing-approvals.html',
  styleUrls: ['./listing-approvals.scss', './listing-moderation.scss'],
})
export class ListingApprovals implements OnInit {
  private readonly productsService = inject(ProductsService);
  private readonly route = inject(ActivatedRoute);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly currentUser = inject(CurrentUserService);
  canReview(action: 'Approve' | 'Hold' | 'Reject'): boolean {
    return this.currentUser.hasPermission(action === 'Approve' ? 'LISTING_REVIEW_APPROVE' : action === 'Hold' ? 'LISTING_REVIEW_HOLD' : 'LISTING_REVIEW_REJECT');
  }

  listings: Product[] = [];
  product?: Product;
  note = '';
  isLoading = false;
  isSaving = false;
  savingAction?: 'Approve' | 'Hold' | 'Reject';
  errorMessage = '';
  successMessage = '';
  selectedQueue: ReviewQueueStatus = 'pending';
  counts: Record<ReviewQueueStatus, number> = { pending: 0, reported: 0, active: 0, rejected: 0 };
  countsLoading = true;
  readonly isDetailMode = Boolean(this.route.snapshot.paramMap.get('id'));
  private queueRequestId = 0;
  private queueSubscription?: Subscription;
  private readonly queueCache = new Map<ReviewQueueStatus, Product[]>();

  ngOnInit(): void {
    const productId = this.route.snapshot.paramMap.get('id');
    if (productId) this.loadDetail(productId);
    else {
      this.loadCounts();
      this.loadQueue();
    }
  }

  private loadCounts(): void {
    this.countsLoading = true;
    this.productsService.reviewCounts().pipe(finalize(() => {
      this.countsLoading = false;
      this.changeDetector.detectChanges();
    })).subscribe({
      next: (counts) => {
        this.counts = counts;
        this.changeDetector.detectChanges();
      },
      error: () => {
        // Queue responses still provide a backward-compatible count fallback.
      },
    });
  }

  loadQueue(queue = this.selectedQueue): void {
    const requestId = ++this.queueRequestId;
    this.queueSubscription?.unsubscribe();
    this.selectedQueue = queue;
    const cachedListings = this.queueCache.get(queue);
    this.listings = cachedListings ?? [];
    this.errorMessage = '';
    this.isLoading = !cachedListings;
    this.queueSubscription = this.productsService.reviewQueue(queue).pipe(finalize(() => {
      if (requestId === this.queueRequestId) {
        this.isLoading = false;
        this.changeDetector.detectChanges();
      }
    })).subscribe({
      next: (result) => {
        if (requestId !== this.queueRequestId || queue !== this.selectedQueue) return;
        this.listings = result.products;
        this.queueCache.set(queue, result.products);
        this.counts = result.hasCompleteCounts
          ? result.counts
          : { ...this.counts, [queue]: result.products.length };
        this.isLoading = false;
        this.changeDetector.detectChanges();
      },
      error: (error: unknown) => {
        if (requestId === this.queueRequestId) this.errorMessage = httpErrorMessage(error, 'Unable to load review queue.');
      },
    });
  }

  loadDetail(productId: string): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.productsService.reviewDetail(productId).pipe(finalize(() => {
      this.isLoading = false;
      this.changeDetector.detectChanges();
    })).subscribe({
      next: (product) => {
        this.product = product;
        this.note = product.moderationNote ?? '';
        this.isLoading = false;
        this.changeDetector.detectChanges();
      },
      error: (error: unknown) => {
        this.errorMessage = httpErrorMessage(error, 'Unable to load listing details.');
        this.changeDetector.detectChanges();
      },
    });
  }

  review(action: 'Approve' | 'Hold' | 'Reject'): void {
    if (!this.product) return;
    if (action !== 'Approve' && !this.note.trim()) {
      this.errorMessage = `Add a reviewer note before choosing ${action === 'Hold' ? 'On hold' : 'Reject'}.`;
      return;
    }
    const previousProduct = this.product;
    const nextStatus: Product['status'] = action === 'Approve' ? 'Active' : action === 'Reject' ? 'Rejected' : 'OnHold';
    this.product = {
      ...this.product,
      status: nextStatus,
      moderationNote: this.note.trim() || this.product.moderationNote,
    };
    this.isSaving = true;
    this.savingAction = action;
    this.errorMessage = '';
    this.successMessage = action === 'Approve'
      ? 'Publishing listing…'
      : action === 'Reject'
        ? 'Rejecting listing…'
        : 'Placing listing on hold…';
    this.changeDetector.detectChanges();
    this.productsService.moderate(this.product.id, action, this.note.trim() || undefined)
      .pipe(finalize(() => {
        this.isSaving = false;
        this.savingAction = undefined;
        this.changeDetector.detectChanges();
      }))
      .subscribe({
        next: () => {
          if (!this.product) return;
          this.product = {
            ...this.product,
            status: nextStatus,
            moderationNote: this.note.trim() || this.product.moderationNote,
            reviewedAt: new Date().toISOString(),
          };
          this.successMessage = action === 'Approve'
            ? 'Listing approved and published successfully.'
            : action === 'Reject'
              ? 'Listing rejected. The seller can see your reason.'
              : 'Listing placed on hold. The seller can see what must be corrected.';
          this.changeDetector.detectChanges();
        },
        error: (error: unknown) => {
          this.product = previousProduct;
          this.successMessage = '';
          this.errorMessage = httpErrorMessage(error, 'Unable to save review decision. The previous status was restored.');
          this.changeDetector.detectChanges();
        },
      });
  }

  status(product: Product): string {
    const value = product.status;
    const numeric = Number(value);
    if (!Number.isNaN(numeric) && String(value).trim() !== '') return ({ 1: 'Draft', 2: 'Active', 3: 'Sold', 4: 'Inactive', 5: 'Rejected', 6: 'On hold', 7: 'Reported', 8: 'Pending review' } as Record<number, string>)[numeric] ?? String(value);
    return value === 'OnHold' ? 'On hold' : String(value);
  }

  condition(product: Product): string {
    const value = product.condition ?? product.listingType;
    return typeof value === 'number' ? (value === 1 ? 'New' : 'Used') : String(value ?? 'Unspecified');
  }

  imageUrl(image: string | ProductImage): string {
    return typeof image === 'string' ? image : image.imageUrl || image.url || image.fileUrl || '';
  }

  location(product: Product): string {
    return [product.addressLine1, product.area, product.city, product.state, product.zipCode, product.country]
      .filter(Boolean).join(', ');
  }

  money(value: number | null | undefined): string {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value ?? 0);
  }

  catalogName(name: string | null | undefined): string {
    return name?.trim() || 'Not specified';
  }

  actionAvailable(action: 'Approve' | 'Hold' | 'Reject'): boolean {
    if (!this.product) return false;
    const currentStatus = this.status(this.product);
    if (action === 'Approve') return currentStatus !== 'Active';
    if (action === 'Reject') return currentStatus !== 'Rejected';
    return currentStatus !== 'On hold';
  }

  emptyState(): { icon: string; title: string; message: string } {
    switch (this.selectedQueue) {
      case 'reported':
        return { icon: 'outlined_flag', title: 'No reported listings', message: 'There are no open customer reports requiring action.' };
      case 'active':
        return { icon: 'inventory_2', title: 'No active listings', message: 'No approved listings are currently published.' };
      case 'rejected':
        return { icon: 'block', title: 'No rejected listings', message: 'No listings have been rejected.' };
      default:
        return { icon: 'task_alt', title: 'No pending listings', message: 'All submitted listings have been reviewed.' };
    }
  }

  queueHeading(): { title: string; description: string } {
    switch (this.selectedQueue) {
      case 'reported': return { title: 'Reported listings', description: 'Investigate customer reports and take appropriate action.' };
      case 'active': return { title: 'Active listings', description: 'Review products that are currently published in the marketplace.' };
      case 'rejected': return { title: 'Rejected listings', description: 'View declined listings and their moderation reasons.' };
      default: return { title: 'Listing approvals', description: 'Review exceptions that require a manual moderation decision.' };
    }
  }

  moderationFlags(product: Product): Array<{ code: string; message: string; score: number; severity: string }> {
    if (!product.moderationFlagsJson) return [];
    try { return JSON.parse(product.moderationFlagsJson); } catch { return []; }
  }
}
